import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import {
  useGetApiV5FirmsAdminCpaSettingsKey,
  usePatchApiV5FirmsAdminCpaSettingsKey,
  getGetApiV5FirmsAdminCpaSettingsKeyQueryKey,
} from '@liscio/api';

export type ReminderType = 'remind_to_pay' | 'notify_withdrawal' | 'no_reminder';

export const REMINDER_TYPE_OPTIONS = [
  {
    value: 'remind_to_pay' as ReminderType,
    label: 'Remind to Pay',
    description:
      'Send a reminder to the client when estimated tax payments are due.',
  },
  {
    value: 'notify_withdrawal' as ReminderType,
    label: 'Notify of Withdrawal',
    description:
      'Notify the client that the firm will withdraw the payment on their behalf.',
  },
  {
    value: 'no_reminder' as ReminderType,
    label: "Don't Remind",
    description: 'No reminder or notification will be sent to the client.',
  },
] as const;

const CPA_SETTING_KEYS = {
  KBA: 'tax_delivery_kba',
  REMINDER_TYPE: 'tax_delivery_reminder_type',
} as const;

/**
 * Hook to fetch and update the KBA setting for tax delivery
 */
export function useTaxDeliveryKba() {
  const queryClient = useQueryClient();
  const queryKey = getGetApiV5FirmsAdminCpaSettingsKeyQueryKey(CPA_SETTING_KEYS.KBA);

  const query = useGetApiV5FirmsAdminCpaSettingsKey(CPA_SETTING_KEYS.KBA, {
    query: {
      select: (data) => Boolean(data?.data),
      staleTime: 1000 * 60 * 60,
    },
  });

  const mutation = usePatchApiV5FirmsAdminCpaSettingsKey({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey });
      },
      onError: () => {
        toast.error('Failed to update KBA setting');
      },
    },
  });

  const updateKba = (enabled: boolean) => {
    mutation.mutate({
      key: CPA_SETTING_KEYS.KBA,
      data: { type: 'boolean', value: [String(enabled)] },
    });
  };

  return {
    kba: (query.data as boolean) ?? false,
    isLoading: query.isLoading,
    isError: query.isError,
    updateKba,
    isUpdating: mutation.isLoading,
  };
}

/**
 * Hook to fetch and update the reminder type setting for tax delivery
 */
export function useTaxDeliveryReminderType() {
  const queryClient = useQueryClient();
  const queryKey = getGetApiV5FirmsAdminCpaSettingsKeyQueryKey(CPA_SETTING_KEYS.REMINDER_TYPE);

  const query = useGetApiV5FirmsAdminCpaSettingsKey(CPA_SETTING_KEYS.REMINDER_TYPE, {
    query: {
      select: (data) => (data?.data as ReminderType) ?? 'remind_to_pay',
      staleTime: 1000 * 60 * 60,
    },
  });

  const mutation = usePatchApiV5FirmsAdminCpaSettingsKey({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey });
      },
      onError: () => {
        toast.error('Failed to update reminder type setting');
      },
    },
  });

  const updateReminderType = (reminderType: ReminderType) => {
    mutation.mutate({
      key: CPA_SETTING_KEYS.REMINDER_TYPE,
      data: { type: 'string', value: [reminderType] },
    });
  };

  return {
    reminderType: (query.data as ReminderType) ?? 'remind_to_pay',
    isLoading: query.isLoading,
    isError: query.isError,
    updateReminderType,
    isUpdating: mutation.isLoading,
  };
}

/**
 * Convenience hook that returns both tax delivery settings
 */
export function useTaxDeliverySettings() {
  const kbaSettings = useTaxDeliveryKba();
  const reminderSettings = useTaxDeliveryReminderType();

  return {
    kba: kbaSettings.kba,
    reminderType: reminderSettings.reminderType,
    isLoading: kbaSettings.isLoading || reminderSettings.isLoading,
    isError: kbaSettings.isError || reminderSettings.isError,
    updateKba: kbaSettings.updateKba,
    updateReminderType: reminderSettings.updateReminderType,
    isUpdating: kbaSettings.isUpdating || reminderSettings.isUpdating,
  };
}
